import './index.css'
import orderBy from 'lodash/orderBy'
import dayjs from '@disco/utils/dayjs'

import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Link, Outlet } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import type { RecipientListId } from 'types/list'
import { exportEmailGroup } from '@disco/state-management/actions/EmailGroupActions'
import { getUser } from '@disco/state-management/selectors/user'
import EmptyMessage from 'components/common/empty-message'
import DocumentHeadTags from 'components/document-head-tags'
import DownloadModal from 'components/modals/download-modal'
import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Spinner from 'components/ui/spinner'
import Tooltip from 'components/ui/tooltip'
import ContactsTable from 'pages/contacts/list/contacts-table'
import {
  CAPTION_EXISTING,
  CAPTION_EXPORT,
  CAPTION_IMPORT,
  CAPTION_SINGLE,
} from '../add/buttons'
import IconEmptyLists from './icon-empty-lists'

const ContactsMailingLists = (): null | React.ReactNode => {
  const emailGroups = useSelector(state => state.emailGroups)
  const isAdmin = useSelector(getUser)?.is_admin
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [deferred, setDeferred] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exportId, setExportId] = useState<RecipientListId | null>(null)

  const exportClick = async (id: RecipientListId) => {
    setLoading(true)
    setError(false)
    setDeferred(false)
    setDownloadUrl(null)
    openModal()
    const result: Record<string, any> = await dispatch(exportEmailGroup(id))

    if (result?.deferred) {
      setDeferred(true)
      setLoading(false)
    }

    if (result?.download_url) {
      setDownloadUrl(result?.download_url)
      setLoading(false)
    }

    if (result?.error) {
      setError(true)
      setLoading(false)
    }
  }

  const openModal = () => setModalOpen(true)

  const closeModal = () => setModalOpen(false)

  const renderModal = () => {
    if (!modalOpen) return null

    if (downloadUrl) {
      return (
        <DownloadModal type="base" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (loading) {
      return (
        <DownloadModal type="loading" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (error && exportId) {
      return (
        <DownloadModal
          type="error"
          onClose={closeModal}
          onClick={() => exportClick(exportId)}
        />
      )
    }

    if (deferred) {
      return <DownloadModal type="deferred" onClose={closeModal} />
    }
  }

  if (emailGroups.isFetchingMailingLists) return <Spinner />
  if (!emailGroups.mailingListsFetched) return null
  const createNewListButton = (
    <Button
      appearance="primary"
      link="/lists/create"
      caption="Create new list"
    />
  )
  let content

  if (emailGroups.mailingList.length > 0) {
    content = (
      <ContactsTable
        columns={[
          {
            id: 'name',
            label: 'Name',
            value: 'LongName',
            renderData: g => (
              <Link className="ContactCell_Link" to={`/lists/${g.id}`}>
                <span translate="no">{g['name']}</span>
              </Link>
            ),
          },
          {
            id: 'recipients',
            label: 'Contacts',
            value: 'Contacts',
            renderData: g => (
              <Link className="ContactCell_Link" to={`/lists/${g.id}`}>
                {g.count_recipients ?? 0}
              </Link>
            ),
          },
          {
            id: 'created',
            label: 'Created',
            value: 'Created',
            renderData: g => (
              <div title={dayjs(g.created).format('MMMM Do YYYY, h:mma')}>
                {dayjs(g.created).format('MMMM Do YYYY')}
              </div>
            ),
          },
          {
            id: 'del',
            label: 'Action',
            value: 'Action',
            renderData: g => (
              <EllipsisMenu
                compact
                options={[
                  {
                    label: CAPTION_EXISTING,
                    value: CAPTION_EXISTING,
                    onClick: () => navigate(`/contacts/list?append=${g.id}`),
                  },
                  {
                    label: CAPTION_IMPORT,
                    value: CAPTION_IMPORT,
                    onClick: () => navigate(`/lists/${g.id}/file-import`),
                  },
                  {
                    label: CAPTION_SINGLE,
                    value: CAPTION_SINGLE,
                    onClick: () => navigate(`/lists/${g.id}/add-single`),
                  },
                  {
                    label: CAPTION_EXPORT,
                    value: CAPTION_EXPORT,
                    disabled: !isAdmin,
                    nodeRight: !isAdmin && (
                      <Tooltip content="Only admins can export contacts.">
                        <Icon type="info-outline" width={16} height={16} />
                      </Tooltip>
                    ),
                    onClick: () => {
                      setExportId(g.id)
                      exportClick(g.id)
                    },
                  },
                  {
                    label: 'Delete',
                    value: 'Delete',
                    destructive: true,
                    onClick: () => navigate(`/lists/delete-list/${g.id}`),
                  },
                ]}
                icon="ellipsis"
                hasArrow={false}
              />
            ),
          },
        ]}
        // @ts-expect-error
        contacts={orderBy(emailGroups.mailingList, 'created', 'desc')}
        rowRenderer="default"
      />
    ) // <>
    //   <TableHead columns={columns} />
    //   {orderBy(emailGroups.emailGroups, 'created', 'desc').map(gr => (
    //     <TableRow
    //       key={gr.id}
    //       columns={columns.map(c => ({
    //         id: c.id,
    //         value: c.valueFn(gr),
    //       }))}
    //     />
    //   ))}
    // </>
  } else {
    content = (
      <div className="ContactsMailingLists_EmptyMsg">
        <EmptyMessage
          icon={<IconEmptyLists />}
          header="No mailing lists yet"
          cta={
            <div>
              Create your first mailing list by clicking the button below.
              <div className="ContactsMailingLists_EmptyBtnCont">
                {createNewListButton}
              </div>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <>
      <DocumentHeadTags title="Mailing Lists | DISCO" />
      <PageHeader title="Mailing Lists" actions={createNewListButton} />
      <PageContainer>
        {content}
        <Outlet />
      </PageContainer>
      {renderModal()}
    </>
  )
}

export default ContactsMailingLists
